<template>
  <div class="signWrap">
    <div class="backBg"></div>

    <div v-if="authType !== OrgAuthType.NONE" class="container flex col f-center gap-32">
      <div class="signTop">
        <NuxtLink :to="{ name: 'index' }" class="logo">
          <img src="~/assets/images/logo_blue.svg" alt="codepresso logo" />
        </NuxtLink>
      </div>

      <LazyOrgAuthEmailForm
          :auth-type="authType"
          :organization-name="organizationName"
          @success="onClickStart"
      />

      <LazySignCommonFooter />
    </div>
  </div>
</template>

<script setup lang="ts">
import {computed, ref, watch} from 'vue';
import {useI18n} from 'vue-i18n';
import {useRoute, useRouter} from '#imports';
import {useOrganizationService} from '~/composables/service/useOrganizationService';
import {useToastStore} from '~/store/toast/toastStore';
import {ToastType} from '~/components/Common/Toast/model/ToastType';
import { useGenericDialog } from '~/composables/useGenericDialog';
import {removeCookie, setCookie} from '~~/package/utils/CookieUtil';
import {CookieKeyType} from '~~/package/mapper/common/model/CookieKeyType';
import {SignType, SignUIType} from '~/components/Sign/enum/SignType';
import {RouteName} from '~~/package/enum/RouteName';
import {OrgAuthType} from '~/components/Org/auth/OrgAuthType';
import {OrgInviteErrorType} from '~/components/Org/auth/OrgInviteErrorType';
import type OrganizationAcceptInviteMemberResponse
  from '~~/package/apis/organization/response/OrganizationAcceptInviteMemberResponse';
import {useUserService} from '~/composables/service/useUserService';
import axios, {type AxiosError} from 'axios';

const { t } = useI18n();
const route = useRoute();
const router = useRouter();

const organizationService = useOrganizationService();
const toastStore = useToastStore();
const { openGenericAlert } = useGenericDialog();

const authType = ref<OrgAuthType>(OrgAuthType.NONE);
const organizationName = ref("");

const userUuid = computed(() => String(route.query.userUuid || "").trim());
const orgUuid = computed(() => String(route.query.orgUuid || "").trim());
const orgName = computed(() => String(route.query.orgName || "").trim());
const isRegistered = computed(() => String(route.query.isRegistered).toLowerCase() === "true");

const setRedirectCookies = () => {
  setCookie(
      CookieKeyType.REDIRECT_URL,
      {
        fullPath: route.fullPath,
        path: route.path,
        query: route.query
      },
      { expires: new Date(Date.now() + 86400 * 1000), maxAge: 86400 }
  );

  if (!isRegistered.value) {
    setCookie(
        CookieKeyType.AUTH_ORG_EMAIL_SIGNUP_CONTEXT,
        { from: "auth-org-email", createdAt: Date.now() },
        { expires: new Date(Date.now() + 86400 * 1000), maxAge: 86400 }
    );
  }
};

const goToSignPage = async () => {
  setRedirectCookies();

  toastStore.openToast({
    toastType: ToastType.WARNING,
    contents: isRegistered.value ? t("account-required") : t('account-up-required'),
  });

  await router.replace({
    path: "/sign",
    query: {
      uiType: isRegistered.value ? SignUIType.LOGIN : SignUIType.SIGNUP,
      type: SignType.ORGANIZATIONS,
      userEmail: route.query.userEmail,
    },
  });
};

const logoutAndGoToSign = async () => {
  // SEC-1 (TSK-10568): clear server-side HttpOnly cookie + legacy JS cookie.
  await useUserService().userSignOut();
  removeCookie(CookieKeyType.ACCESS_TOKEN);
  removeCookie(CookieKeyType.OPEN_CREDIT_BANNER);
  useUserService().userLogOut();
  await goToSignPage();
};

const openOrganizationAccountMissMatch = () => {
  openGenericAlert(
    { title: t("skillCertify-account-miss-match"), contents: t("skillCertify-account-miss-match-description"), popupClass: 'dark' },
    { confirm: { title: t("settings-logout"), event: logoutAndGoToSign }, close: { event: logoutAndGoToSign } }
  );
};

const acceptInvitation = async () => {
  authType.value = OrgAuthType.NONE;
  organizationName.value = orgName.value || "-";

  if (!userUuid.value) {
    authType.value = OrgAuthType.FAILED;
    return;
  }

  try {
    const res: OrganizationAcceptInviteMemberResponse | null =
        await organizationService.acceptInviteMember(userUuid.value, orgUuid.value);

    if (res?.success) {
      authType.value = OrgAuthType.JOIN;
      return;
    }
  } catch (err: unknown) {
    authType.value = OrgAuthType.FAILED;

    const errorType =
        axios.isAxiosError(err)
            ? err.response?.data?.data?.errorType ?? OrgInviteErrorType.INVALID_EMAIL_FORMAT
            : OrgInviteErrorType.INVALID_EMAIL_FORMAT;

    if (errorType === OrgInviteErrorType.ACCOUNT_MISMATCH) {
      openOrganizationAccountMissMatch();
      return;
    }
  }
};

const onClickStart = () => {
  router.push({ name: RouteName.Dashboard });
};

watch(() => route.query, () => {
  acceptInvitation();
}, { immediate: true });
</script>

<style>
@import "~/assets/scss/form_style.scss";
</style>